import {
  CLUSTER_TOLERANCE,
  type ClusterInfo,
  HILL_BASELINE_Y,
  HILL_VIEWBOX,
  LABEL_FLIP_THRESHOLD_X,
  buildHillPath,
  clampHillX,
  clusterDots,
  hillXToSvgX,
  hillYFromX,
  svgXToHillX,
  tierYOffset,
} from '@/lib/hill-math';
import { useAnimatedHillX } from '@/lib/use-animated-hill-x';
import { useMoveDot } from '@/lib/use-move-dot';
import { type Dot, type HillChart, isDotHammered } from '@hillchart/shared';
import { type KeyboardEvent, type PointerEvent, useRef, useState } from 'react';

interface HillProps {
  /** The hill chart to render — used for both its id (for the mutation) and its dots. */
  hill: HillChart;
  /**
   * Opaque token identifying the current *view* (live vs. a specific
   * snapshot). Passed down to each `HillDot` so `useAnimatedHillX` can
   * distinguish "user switched view → animate along the curve" from
   * "in-view update → snap instantly" (drag commit, WS echo). The caller
   * typically passes `selectedSnapshotId ?? 'live'`.
   */
  viewId: string;
  /**
   * When true, the chart renders as a static viewer: no pointer drag, no
   * keyboard nav, no `role="slider"` on the dots. Used by the snapshot
   * panel to display a frozen past state identically to the live view
   * without allowing mutations. The dot visuals (position, color, label)
   * are unchanged so the two modes are pixel-comparable.
   */
  readonly?: boolean;
}

/**
 * Drag state is tracked *locally* rather than via the cache. Writing every
 * pointermove into the TanStack Query cache would re-render every subscriber
 * (HillView header, SnapshotPanel, etc.) 60×/s. Instead, the dragged dot
 * renders from `drag.x` while all others render from `hill.dots`, and the
 * cache only learns about the move at pointerup via `useMoveDot`.
 */
interface DragState {
  dotId: string;
  /** Hill position (0–100), not SVG units. */
  x: number;
}

/**
 * SVG hill chart: curve + draggable dots with keyboard navigation.
 *
 * Design choices worth preserving (Phase D, CIR-009):
 *
 * 1. Pointerup-only persistence. Dragging at 60 fps would spam the backend
 *    and the WS hub; we accumulate the move locally and fire exactly one
 *    PATCH when the user releases. The server's echo (next step) becomes
 *    the authoritative write.
 *
 * 2. Pointer capture on the dot (not the SVG root). This guarantees we keep
 *    receiving pointermove / pointerup even when the cursor leaves the dot
 *    — or the viewBox entirely — without us having to hand-roll window
 *    listeners. `releasePointerCapture` on pointerup lets the browser
 *    resume normal hit-testing for the next interaction.
 *
 * 3. Client→hill coordinate conversion via `getScreenCTM().inverse()`.
 *    Naïve `clientX - rect.left` math breaks the moment the SVG is scaled
 *    (container resize, CSS transforms). The CTM-based path is the only
 *    one that stays correct across viewport widths without us hand-coding
 *    the aspect-ratio rules.
 *
 * 4. No-op guard before mutate. A click-without-drag (pointerdown +
 *    pointerup on the same pixel) would otherwise fire a PATCH that sets x
 *    to its current value — wasted round-trip, wasted WS broadcast, and a
 *    spurious `lastMovedAt` bump that pollutes the "who moved what" log.
 *
 * 5. Keyboard uses direct mutate, not drag state. Arrow keys are discrete;
 *    the optimistic update in `useMoveDot` already gives them visual
 *    feedback without a local staging layer. Shift amplifies the step to
 *    ±5 for fast rough positioning; Home/End jump to the feet.
 */
export function Hill({ hill, viewId, readonly = false }: HillProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  // The mutation hook is always initialised (React hook rules forbid
  // conditional calls). In readonly mode it simply stays unused — no
  // handler ever invokes `moveDot.mutate`. The cost is a single closure;
  // the benefit is that swapping `readonly` at runtime (e.g. when the
  // snapshot panel toggles back to live) doesn't remount the SVG tree.
  const moveDot = useMoveDot(hill.id);
  const [drag, setDrag] = useState<DragState | null>(null);

  // Cluster assignment: which dots overlap closely enough on the hill to
  // need vertical fanning, and at what tier within their cluster. Computed
  // straight from `hill.dots` with no `useMemo` because (a) `clusterDots`
  // is O(n log n) for a tiny n (≤ 9 in practice — this codebase is sized
  // for Shape Up scopes, not dashboards), and (b) wrapping in `useMemo`
  // with `hill.dots` as a dep would invalidate every WebSocket echo
  // anyway. The `Map` lookup in <HillDot> is O(1).
  const clusters = clusterDots(hill.dots, CLUSTER_TOLERANCE);

  /**
   * Convert a client-space pointer position into a hill position (0–100).
   * Returns `null` if we can't (SVG not mounted, no CTM) — caller skips.
   */
  function clientToHillX(clientX: number, clientY: number): number | null {
    const svg = svgRef.current;
    if (svg === null) return null;
    const ctm = svg.getScreenCTM();
    if (ctm === null) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const { x } = pt.matrixTransform(ctm.inverse());
    return clampHillX(svgXToHillX(x));
  }

  function handlePointerDown(event: PointerEvent<SVGGElement>, dotId: string): void {
    // Only primary-button drags start a move. Middle/right-click should
    // pass through to the browser's default (context menu, etc.).
    if (event.button !== 0) return;
    // preventDefault suppresses mouse-triggered focus on the <g>. Without
    // it, Chromium caches the focus ring's bounding box at the pointerdown
    // position; subsequent `transform` updates move the dot but not the
    // ring, leaving a ghost outline at the original spot until we blur.
    // Keyboard focus (Tab) still works — `tabIndex={0}` is unaffected.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const hillX = clientToHillX(event.clientX, event.clientY);
    if (hillX === null) return;
    setDrag({ dotId, x: hillX });
  }

  function handlePointerMove(event: PointerEvent<SVGGElement>): void {
    if (drag === null) return;
    // Sticky-drag defence: if we still think we're dragging but no mouse
    // button is currently held, a `pointerup` escaped us — classic
    // scenarios are the user releasing outside the browser window, the
    // OS swallowing the event during an alt-tab, or the browser silently
    // revoking our pointer capture. Without this guard, every subsequent
    // pointermove over a dot would keep sliding it under an unpressed
    // cursor ("drag without holding the button"). `event.buttons === 0`
    // is the canonical check and works uniformly for mouse, pen, and
    // touch (no contact → 0). We synthesise a clean pointerup against
    // the *originally-dragged* dot (looked up by id) rather than the
    // currently-hovered one, which may not be the same if capture was
    // lost mid-flight.
    if (event.buttons === 0) {
      const activeDot = hill.dots.find((d) => d.id === drag.dotId);
      if (activeDot === undefined) {
        setDrag(null);
        return;
      }
      handlePointerUp(event, activeDot);
      return;
    }
    const hillX = clientToHillX(event.clientX, event.clientY);
    if (hillX === null) return;
    setDrag({ dotId: drag.dotId, x: hillX });
  }

  function handlePointerUp(event: PointerEvent<SVGGElement>, dot: Dot): void {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // Safety net for the focus-ring ghost bug: if preventDefault didn't
    // fully suppress focus (browser variance on SVG), blur now so the
    // ring — if any — disappears with the drag rather than staying pinned
    // to the pointerdown position.
    event.currentTarget.blur();
    if (drag === null || drag.dotId !== dot.id) {
      setDrag(null);
      return;
    }
    const finalX = drag.x;
    // Skip the PATCH if the drag ended exactly where it started — avoids
    // needless `lastMovedAt` bumps on a click-without-drag.
    if (finalX === dot.x) {
      setDrag(null);
      return;
    }
    // Do NOT clear drag synchronously here. Rationale: `mutate()` kicks off
    // `onMutate`, whose `setQueryData` runs in a microtask (after the
    // awaited `cancelQueries`). If we nulled drag now, the render between
    // pointerup and the cache landing would read `dot.x` — still the
    // pre-drag value — and the dot would flash back to the pointerdown
    // position for one frame before snapping to finalX. Keeping drag
    // alive pins the render at finalX across the round-trip; onSettled
    // clears it once the cache is authoritative (success or rolled back).
    moveDot.mutate(
      { dotId: dot.id, body: { x: finalX } },
      {
        onSettled: () => {
          // Re-drag safety: if the user started dragging this dot again
          // before our mutation settled, `current.x` would differ from the
          // value we released at. Only clear when drag is still pinned to
          // exactly what we committed.
          setDrag((current) => {
            if (current?.dotId === dot.id && current.x === finalX) {
              return null;
            }
            return current;
          });
        },
      },
    );
  }

  /**
   * Cancel an in-progress drag without committing. Fires when the browser
   * aborts the pointer sequence (tab change, touch-to-scroll in some cases).
   */
  function handlePointerCancel(event: PointerEvent<SVGGElement>): void {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDrag(null);
  }

  function handleKeyDown(event: KeyboardEvent<SVGGElement>, dot: Dot): void {
    const step = event.shiftKey ? 5 : 1;
    let targetX: number | null = null;
    switch (event.key) {
      case 'ArrowLeft':
        targetX = clampHillX(dot.x - step);
        break;
      case 'ArrowRight':
        targetX = clampHillX(dot.x + step);
        break;
      case 'Home':
        targetX = 0;
        break;
      case 'End':
        targetX = 100;
        break;
      default:
        return;
    }
    // preventDefault stops arrow keys from scrolling the page while the
    // dot has focus — a subtle but essential UX fix.
    event.preventDefault();
    if (targetX !== dot.x) {
      moveDot.mutate({ dotId: dot.id, body: { x: targetX } });
    }
  }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${HILL_VIEWBOX.width} ${HILL_VIEWBOX.height}`}
      className="h-auto w-full touch-none select-none"
      role="img"
      aria-label="Hill chart"
    >
      <title>Hill chart</title>
      {/* Baseline: the horizontal axis dots return to at x=0 and x=100. */}
      <line
        x1={HILL_VIEWBOX.paddingX}
        y1={HILL_BASELINE_Y}
        x2={HILL_VIEWBOX.width - HILL_VIEWBOX.paddingX}
        y2={HILL_BASELINE_Y}
        className="stroke-slate-200"
        strokeWidth={1}
      />

      {/* Crest divider — the vertical dashed line that splits the hill in
          two at x=50, from the curve's peak down to the baseline. This is
          the Shape Up signature marker: left of the crest is "figuring
          out", right of the crest is "getting it done". Drawn before the
          curve so the curve line sits visually on top at the peak, and
          before the dots so a dot sitting at x=50 renders over the line
          rather than behind it. */}
      <line
        x1={hillXToSvgX(50)}
        x2={hillXToSvgX(50)}
        y1={hillYFromX(50)}
        y2={HILL_BASELINE_Y}
        className="stroke-slate-300"
        strokeWidth={1.5}
        strokeDasharray="6 6"
      />

      {/* The hill curve itself — same math as hillYFromX, so the dots
          always sit exactly on the line rather than near it. */}
      <path d={buildHillPath()} fill="none" strokeWidth={2} className="stroke-slate-300" />

      {/* Foot labels — Shape Up vocabulary ("Figuring out what to do" vs.
          "Getting it done"). Positioned below the baseline so they don't
          clash with dot labels above the curve. Sized at text-base with
          medium weight so they read as a primary chart legend rather than
          a fine-print caption — matching the Basecamp reference schema. */}
      <text
        x={HILL_VIEWBOX.paddingX}
        y={HILL_BASELINE_Y + 38}
        textAnchor="start"
        className="fill-slate-600 font-medium text-base"
      >
        Figuring out what to do
      </text>
      <text
        x={HILL_VIEWBOX.width - HILL_VIEWBOX.paddingX}
        y={HILL_BASELINE_Y + 38}
        textAnchor="end"
        className="fill-slate-600 font-medium text-base"
      >
        Getting it done
      </text>

      {hill.dots.map((dot) => (
        // Keyed on `externalScopeId`, not `dot.id`. The upstream milestone
        // is the stable business identity of a scope across views: live
        // dots use a UUID, but snapshot dots (`snapshotToHillView`) use a
        // synthetic `${snapshotId}:${milestoneId}` composite. Keying on
        // `dot.id` made every view switch remount each <HillDot> with a
        // fresh `useState`, so the replay animation never kicked in (the
        // hook's `renderedX` started at the target). Keying on the
        // milestone id lets React reconcile the same component across
        // live↔snapshot transitions, so `useAnimatedHillX` sees the
        // `viewId` change and animates from the previous rendered position
        // to the new target. Uniqueness within the rendered list is
        // guaranteed by the backend's UNIQUE(hill_chart_id, external_scope_id).
        <HillDot
          key={dot.externalScopeId}
          dot={dot}
          viewId={viewId}
          readonly={readonly}
          dragX={drag?.dotId === dot.id ? drag.x : null}
          // Cluster info comes from `clusters` (computed once per render
          // above the .map). Falling back to a solo cluster — `tier 0`,
          // `clusterSize 1` — keeps `<HillDot />` self-sufficient if a
          // future caller forgets to feed cluster info; the dot just
          // renders at its natural curve position with no offset.
          cluster={clusters.get(dot.id) ?? { tier: 0, clusterSize: 1 }}
          // Stable per-milestone number persisted as `displayOrder` on
          // the dot. Assigned at insert time and never mutated, so the
          // user sees the same #1, #2, … across live and every
          // historical snapshot. It is a pure label — it does NOT drive
          // any sort or layout decision (positions come from `dot.x`).
          dotNumber={dot.displayOrder}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKeyDown}
        />
      ))}
    </svg>
  );
}

interface HillDotProps {
  dot: Dot;
  viewId: string;
  readonly: boolean;
  /**
   * Local drag position (hill units, 0–100) when this dot is the one
   * currently being dragged by the user, or `null` otherwise. When
   * non-null, it overrides the animated position — the dot tracks the
   * pointer directly at 60 fps without interpolation so the drag feels
   * 1:1, and any animation still running internally is invisible.
   */
  dragX: number | null;
  /**
   * Tier and cluster size for this dot, as computed by `clusterDots` from
   * `hill.dots`. Tier 0 sits exactly on the curve; higher tiers are
   * offset upward by `tierYOffset` so a pile of dots at the same x stays
   * legible. Solo dots receive `{ tier: 0, clusterSize: 1 }` and behave
   * exactly like before this change.
   */
  cluster: ClusterInfo;
  /**
   * 1-based number drawn inside the circle. Persisted as `displayOrder`
   * on the dot, assigned at insert time and never mutated — so the same
   * milestone shows the same number across live, snapshot replays, and
   * archive views.
   */
  dotNumber: number;
  onPointerDown: (event: PointerEvent<SVGGElement>, dotId: string) => void;
  onPointerMove: (event: PointerEvent<SVGGElement>) => void;
  onPointerUp: (event: PointerEvent<SVGGElement>, dot: Dot) => void;
  onPointerCancel: (event: PointerEvent<SVGGElement>) => void;
  onKeyDown: (event: KeyboardEvent<SVGGElement>, dot: Dot) => void;
}

/**
 * One draggable dot on the hill.
 *
 * Factored out of `<Hill />` so we can legally call `useAnimatedHillX`
 * once per dot: hooks can't be called inside a `.map()` whose array
 * length changes at runtime (dots get added/removed when a GitLab project
 * gains or loses milestones), because that would shuffle the hook order
 * across renders. Giving each dot its own component keyed by
 * `externalScopeId` pins hook identity to the underlying scope, so
 * React's hook slots stay aligned whether we render 1 dot or 20 — and,
 * crucially, the same `<HillDot>` instance persists across live↔snapshot
 * switches, letting `useAnimatedHillX` animate between the two positions
 * instead of remounting with a fresh `useState`.
 *
 * The component is otherwise dumb: all pointer/keyboard handlers are
 * passed in from the parent, which owns the drag state and the mutation.
 * Everything here is presentational — position interpolation and click
 * target geometry.
 */
function HillDot({
  dot,
  viewId,
  readonly,
  dragX,
  cluster,
  dotNumber,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onKeyDown,
}: HillDotProps) {
  // Scope-hammering is derived from the milestone name (frozen per
  // snapshot, live for the current view). When true:
  //   - the circle paints slate-400 instead of `dot.color`
  //   - a red X overlays the circle
  //   - pointer / keyboard interactions are suppressed (acts like
  //     `readonly` even on a live hill)
  // Un-hammering symmetrically just removes the override — the original
  // `dot.color` returns and movement is allowed again.
  const hammered = isDotHammered(dot.externalScopeName);

  // Animate along the hill curve when `viewId` changes (live↔snapshot
  // or snapshot↔snapshot). Stays idle — `animatedX` equals `dot.x` — for
  // in-view updates like drag commits and WS echoes.
  const animatedX = useAnimatedHillX(dot.x, viewId);

  // Drag wins over animation: while the user is dragging, the dot must
  // track the pointer exactly. The animation can keep running internally
  // (e.g. if a view switch was in flight when the drag started) without
  // visible bleed; it'll land at its target by the time the user lets go.
  const renderedX = dragX !== null ? dragX : animatedX;
  const svgX = hillXToSvgX(renderedX);
  const baseY = hillYFromX(renderedX);
  // Drag drops the dot to its natural curve position immediately
  // (effective tier = 0). Otherwise we apply the tier offset so dots
  // that share an x stack vertically rather than overlap. The "1:1
  // drag" promise from the design discussion: the moment you grab a
  // tier-3 dot in a pile, it snaps to the curve under your cursor and
  // stays there until release. The other tiers in that pile keep their
  // offset (because their `cluster.tier` doesn't change) — only the
  // dragged dot is liberated.
  const effectiveTier = dragX !== null ? 0 : cluster.tier;
  const svgY = baseY - tierYOffset(effectiveTier, cluster.clusterSize, baseY);
  const isDragging = dragX !== null;
  // Label-side flip: above the threshold, the label renders to the LEFT
  // of the dot (textAnchor="end", negative x) so its right edge anchors
  // to the dot rather than running into the chart's right padding. Below
  // the threshold we keep it on the right (textAnchor="start", positive
  // x). The 16 = dot radius (12) + 4-unit margin so the text doesn't
  // touch the circle stroke.
  const flipsLeft = renderedX > LABEL_FLIP_THRESHOLD_X;

  // Spread the interactive contract only when the chart is live AND
  // the dot is not hammered. In readonly or hammered mode we omit
  // `role="slider"`, `tabIndex`, every pointer / keyboard handler, and
  // the aria-value* trio — the dot becomes a plain labelled graphic.
  // Hammered dots behave exactly like readonly dots interaction-wise:
  // their position is frozen at the moment of hammering and only
  // un-tagging the milestone in GitLab can resurrect drag/keyboard.
  const interactiveProps =
    readonly || hammered
      ? {}
      : {
          tabIndex: 0,
          role: 'slider' as const,
          'aria-valuemin': 0,
          'aria-valuemax': 100,
          'aria-valuenow': Math.round(renderedX),
          onPointerDown: (e: PointerEvent<SVGGElement>) => onPointerDown(e, dot.id),
          onPointerMove,
          onPointerUp: (e: PointerEvent<SVGGElement>) => onPointerUp(e, dot),
          onPointerCancel,
          onKeyDown: (e: KeyboardEvent<SVGGElement>) => onKeyDown(e, dot),
        };

  return (
    <g
      transform={`translate(${svgX} ${svgY})`}
      aria-label={dot.externalScopeName}
      data-dot-id={dot.id}
      data-dragging={isDragging ? 'true' : undefined}
      // No CSS transition on `transform` — `useAnimatedHillX` drives
      // motion in JS, interpolating `x` in hill-space and recomputing
      // `y` on the curve every frame. A CSS transition on the
      // compound `translate(x y)` would interpolate linearly between
      // the start and end points, cutting a chord across the hill
      // (dots would visibly leave the curve mid-animation). Keeping
      // the SVG `transform` updates un-transitioned lets each rendered
      // position sit exactly on `hillYFromX(x)`.
      className={`outline-none focus-visible:[&_circle]:stroke-slate-900 ${
        readonly || hammered ? 'cursor-not-allowed' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      data-hammered={hammered ? 'true' : undefined}
      {...interactiveProps}
    >
      {/*
        Hammered dots paint slate-400 (#94a3b8) — neutral, low-saturation,
        clearly "out of the active palette" without being a death stamp.
        The original `dot.color` is preserved in state so un-hammering
        restores the dot's identity instantly.
      */}
      <circle
        r={12}
        fill={hammered ? '#94a3b8' : dot.color}
        strokeWidth={2}
        className="stroke-white drop-shadow-sm"
      />
      {/* Number rendered inside the circle, vertically centred via
          `dominantBaseline="central"` (no `dy` math — the browser does it
          right at any font size). White fill assumes the palette in
          `DOT_COLORS` keeps WCAG-acceptable contrast against any chosen
          color; that constraint is documented in the palette's source.
          `pointer-events-none` so clicks pass through to the parent
          `<g>` and trigger drag/keyboard handlers — the text is purely
          decorative. */}
      <text
        textAnchor="middle"
        dominantBaseline="central"
        className="pointer-events-none fill-white font-medium text-[11px]"
      >
        {dotNumber}
      </text>
      {hammered && (
        // Red X overlay covering the circle. Drawn on top of the number
        // so the "this is cut" signal reads first; the milestone name
        // remains fully readable in the side label below. We don't add
        // `aria-hidden` here: the parent `<g>` carries the
        // `aria-label="[SH] …"` that already communicates the state to
        // screen readers, and SVG `<path>` is not part of the default
        // accessibility tree, so the cross is silent by construction.
        // Biome's `noAriaHiddenOnFocusable` would also reject the
        // attribute since static analysis can't tell whether the
        // ancestor is focusable in this branch.
        <path
          d="M-6 -6 L6 6 M-6 6 L6 -6"
          stroke="#dc2626"
          strokeWidth={2.5}
          strokeLinecap="round"
          className="pointer-events-none"
          data-testid="hammered-cross"
        />
      )}
      {/* Inline label: rendered to the SIDE of the dot rather than above
          it. Right-anchored vertically to the dot's center via
          `dominantBaseline="central"`, and on the right by default; flips
          to the left past `LABEL_FLIP_THRESHOLD_X` so long names near
          the right foot of the hill don't clip out of the viewBox. The
          companion legend below the chart (rendered by the route, not
          this component) provides a stable number-to-name reference for
          users who want to scan the full set without reading every
          inline label. */}
      <text
        x={flipsLeft ? -16 : 16}
        textAnchor={flipsLeft ? 'end' : 'start'}
        dominantBaseline="central"
        className="pointer-events-none fill-slate-700 font-medium text-xs"
      >
        {dot.externalScopeName}
      </text>
    </g>
  );
}
